import React, { useState, useEffect } from 'react';
import {
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Menu,
    MenuItem,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useBrands, useEditBrand, useDeleteBrand, useIsOrderAvailable } from '../../hooks/useBrand';
import { useDivision, } from '../../hooks/useDivision';

import { usePermission } from '../../hooks/usePermission';
import DataTable from '../shared/DataTable';
import { EditBrandsPermission, DeleteBrandsPermission } from '../../constants/permissions';
import { useDivisionTeams } from "../../hooks/useTeam";
import { useTeam } from "../../hooks/useTeam";
const BrandsTable = () => {
    const { data: brands, isLoading } = useBrands();
    const { data: divisions } = useDivision();
    const editBrand = useEditBrand();
    const deleteBrand = useDeleteBrand();
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [editFormData, setEditFormData] = useState({
        name: '',
        division_id: '',
        team_id: '',
        order: '',
    });
    const [orderError, setOrderError] = useState('');
    const { data: teams } = useDivisionTeams(editFormData.division_id);
    const { data: isOrderAvailable } = useIsOrderAvailable(
        editFormData.division_id,
        editFormData.team_id,
        editFormData.order ? Number(editFormData.order) : null,
        selectedBrand?.id // exclude current brand when checking
    );
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedActionId, setSelectedActionId] = useState(null);
    const { data: allTeams } = useTeam();
    const { data: canEditBrand } = usePermission({
        permissionName: EditBrandsPermission
    });

    const { data: canDeleteBrand } = usePermission({
        permissionName: DeleteBrandsPermission
    });

    const handleMenuClick = (event, id) => {
        setAnchorEl(event.currentTarget);
        setSelectedActionId(id);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedActionId(null);
    };

    const handleEditClick = (brands) => {
        setSelectedBrand(brands);
        setEditFormData({
            name: brands.name,
            division_id: brands.division_id || '',
            team_id: brands.team_id || '',
            order: brands.order || '',
        });
        setEditDialogOpen(true);
        handleMenuClose();
    };

    const handleEditClose = () => {
        setEditDialogOpen(false);
        setSelectedBrand(null);
        setOrderError('');
    };

    // Check order availability for edit
    useEffect(() => {
        if (editFormData.order && editFormData.division_id && editFormData.team_id && selectedBrand) {
            // Only show error if order changed from original
            if (Number(editFormData.order) !== selectedBrand.order && isOrderAvailable === false) {
                setOrderError('This order number is already taken');
            } else {
                setOrderError('');
            }
        } else {
            setOrderError('');
        }
    }, [isOrderAvailable, editFormData.order, editFormData.division_id, editFormData.team_id, selectedBrand]);

    const getDivisionName = (divisionId) => {
        const division = divisions?.find(st => st.id === divisionId);
        return division?.name || 'N/A';
    };
    
    const getTeamName = (teamId) => {
        const team = allTeams?.find(t => t.id === teamId);
        return team?.name || 'N/A';
    };

    const enhancedBrands = React.useMemo(() => {
        if (!brands) return [];
        return brands.map(brand => ({
            ...brand,
            divisionName: getDivisionName(brand.division_id),
            teamName: getTeamName(brand.team_id),
            order: brand.order,
        }));
    }, [brands, divisions, allTeams]);


    const handleEditSubmit = async () => {
        if (orderError) {
            alert('Please fix the order error before submitting');
            return;
        }
        try {
            await editBrand.mutateAsync({
                id: selectedBrand.id,
                data: editFormData,
            });
            handleEditClose();
        } catch (error) {
            console.error('Error editing brand:', error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this brand?')) {
            try {
                await deleteBrand.mutateAsync(id);
                handleMenuClose();
            } catch (error) {
                console.error('Error deleting brand:', error);
            }
        }
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
        },
        {
            name: 'Division',
            selector: row => row.divisionName,
            sortable: true,
        },
        {
            name: 'Team',
            selector: row => row.teamName,
            sortable: true,
        },
        {
            name: 'Order',
            selector: row => row.order,
            sortable: true,
        },
        {
            name: 'Actions',
            cell: row => (
                <IconButton onClick={(e) => handleMenuClick(e, row.id)}>
                    <MoreVertIcon />
                </IconButton>
            ),
            width: '100px',
        },
    ];

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <DataTable
                columns={columns}
                data={enhancedBrands}
                searchFields={['name', 'divisionName', 'teamName']}
                searchPlaceholder="Search brands..."
            />

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {canEditBrand && (
                    <MenuItem onClick={() => handleEditClick(brands.find(d => d.id === selectedActionId))}>
                        <i className="fa-solid fa-edit text-green-700 mr-2 mb-1" />
                        <h6 className='text-blueColor'>Edit</h6>
                    </MenuItem>
                )}
                {canDeleteBrand && (
                    <MenuItem onClick={() => handleDelete(selectedActionId)}>
                        <i className="fa-solid fa-trash text-red-600 mr-2" />
                        <h6 className='text-blueColor'>Delete</h6>
                    </MenuItem>
                )}
            </Menu>

            <Dialog
                open={editDialogOpen}
                onClose={handleEditClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle className="bg-blueColor text-white py-4">
                    <i className="fas fa-edit text-xl mx-2"></i>
                    <span className="text-xl font-semibold">Edit Brand</span>
                </DialogTitle>
                <DialogContent className="mt-4">
                    <div className="grid grid-cols-1 gap-4 p-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Name
                            </label>
                            <input
                                name="name"
                                value={editFormData.name}
                                onChange={(e) =>
                                    setEditFormData({ ...editFormData, name: e.target.value })
                                }
                                type="text"
                                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Division
                            </label>
                            <select
                                name="division_id"
                                value={editFormData.division_id}
                                onChange={(e) =>
                                    setEditFormData({ ...editFormData, division_id: e.target.value })
                                }
                                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                            >
                                <option value="">Select a division</option>
                                {divisions?.map((division) => (
                                    <option key={division.id} value={division.id}>
                                        {division.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Team
                            </label>
                            <select
                                name="team_id"
                                value={editFormData.team_id}
                                onChange={(e) =>
                                    setEditFormData({ ...editFormData, team_id: e.target.value })
                                }
                                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                            >
                                <option value="">Select a Team</option>
                                {teams?.map((team) => (
                                    <option key={team.id} value={team.id}>
                                        {team.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Order
                            </label>
                            <input
                                name="order"
                                value={editFormData.order}
                                onChange={(e) =>
                                    setEditFormData({ ...editFormData, order: e.target.value ? Number(e.target.value) : '' })
                                }
                                type="number"
                                min="1"
                                placeholder="Enter order number"
                                className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${orderError ? 'border-red-500 focus:ring-red-500' : 'focus:ring-purpleColor'}`}
                            />
                            {orderError && (
                                <p className="text-red-500 text-sm mt-1">{orderError}</p>
                            )}
                        </div>
                    </div>
                    <div className="flex justify-end gap-4 mt-6 p-4">
                        <button
                            onClick={handleEditClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleEditSubmit}
                            className="px-4 py-2 bg-blueColor text-white rounded"
                        >
                            Save
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default BrandsTable; 